import { useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { MoreVertRounded as MoreVertIcon } from '@material-ui/icons';

import { OrganizationDeliveryPoint } from '../../types/organization.type';
import RemoveDeliveryPointDialog from '../Modals/RemoveDeliveryPointDialog.component';
import { useUpdateOrganizationDeliveryPoints } from '../../hooks/update-organization-delivery-points.hook';
import { formatScheduleLines } from '../../../delivery-points/utils/format-schedule-days.util';

type DeliveryPointsTableHeader = {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right';
};

type Props = {
  organizationId: string;
  maxHeight: string;
  deliveryPoints: OrganizationDeliveryPoint[];
  refreshOrganization: () => Promise<void>;
};

const DELIVERY_POINTS_TABLE_HEADERS: DeliveryPointsTableHeader[] = [
  { key: 'name', label: 'Nom', minWidth: 255, align: 'left' },
  { key: 'address', label: 'Adresse', minWidth: 255, align: 'left' },
  { key: 'schedules', label: 'Créneaux', minWidth: 160, align: 'left' },
  { key: 'actions', minWidth: 65, align: 'right' },
];

function DeliveryPointsTableHeaderRow({ headers }: { headers: DeliveryPointsTableHeader[] }) {
  return (
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header.key} align={header.align} style={{ minWidth: header.minWidth, fontWeight: 'bold' }}>
            {header?.label ?? ''}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function DeliveryPointsTableEmptyBody({ headers }: { headers: DeliveryPointsTableHeader[] }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={headers.length} align="center">
          Aucun point de livraison
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

function DeliveryPointTableRow({
  organizationId,
  deliveryPoint,
  deliveryPoints,
  refreshOrganization,
}: {
  organizationId: string;
  deliveryPoint: OrganizationDeliveryPoint;
  deliveryPoints: OrganizationDeliveryPoint[];
  refreshOrganization: () => Promise<void>;
}) {
  const { updateOrganizationDeliveryPoints, isUpdating } = useUpdateOrganizationDeliveryPoints();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleRemoveDeliveryPoint = async () => {
    const remainingDeliveryPointIds = deliveryPoints
      .filter((currentDeliveryPoint) => currentDeliveryPoint.id !== deliveryPoint.id)
      .map((currentDeliveryPoint) => currentDeliveryPoint.id);

    const updatedOrganization = await updateOrganizationDeliveryPoints({
      organizationId,
      deliveryPointIds: remainingDeliveryPointIds,
    });

    if (updatedOrganization) await refreshOrganization();
  };

  return (
    <TableRow hover>
      <TableCell>{deliveryPoint.name}</TableCell>
      <TableCell>
        {deliveryPoint.address.street}, {deliveryPoint.address.zipCode} {deliveryPoint.address.city}
      </TableCell>
      <TableCell>
        {formatScheduleLines(deliveryPoint.schedules).map((scheduleLine) => (
          <span style={{ display: 'block' }} key={scheduleLine}>
            {scheduleLine}
          </span>
        ))}
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" aria-controls="delivery-point-actions" aria-haspopup="true" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="delivery-point-actions"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <RemoveDeliveryPointDialog
            deliveryPoint={deliveryPoint}
            onConfirm={handleRemoveDeliveryPoint}
            trigger={(handleOpen) => (
              <MenuItem onClick={handleOpen} disabled={isUpdating}>
                Supprimer
              </MenuItem>
            )}
          />
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export default function DeliveryPointsTable({ organizationId, maxHeight, deliveryPoints, refreshOrganization }: Props) {
  const hasDeliveryPoints = !!deliveryPoints.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight }}>
        <Table stickyHeader>
          <DeliveryPointsTableHeaderRow headers={DELIVERY_POINTS_TABLE_HEADERS} />
          {!hasDeliveryPoints && <DeliveryPointsTableEmptyBody headers={DELIVERY_POINTS_TABLE_HEADERS} />}
          {hasDeliveryPoints && (
            <TableBody>
              {deliveryPoints.map((currentDeliveryPoint) => (
                <DeliveryPointTableRow
                  key={currentDeliveryPoint.id}
                  organizationId={organizationId}
                  deliveryPoint={currentDeliveryPoint}
                  deliveryPoints={deliveryPoints}
                  refreshOrganization={refreshOrganization}
                />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}
